import { ISupportsRequestHeaderValues } from '@barthauer/abc/core/authentication';
import {
  ParameterContainer,
  ParameterDefinition,
  ParameterValue,
} from '@barthauer/abc/queries/parameters/core';

export enum QueryOutputMode {
  Json,
  GeoJson,
  Xml,
  Bce,
}

export enum QueryColumnSortOrder {
  None = 'NONE',
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export enum SubQueryKind {
  Sub,
  LeftJoin,
  InnerJoin,
}

export class PathDefinition {
  Name: string;
  Path: string;
}

export class ValueDefinition {
  Name: string;
  OPath: string;
}

export class ModelBase implements ISupportsRequestHeaderValues {
  OutputMode?: QueryOutputMode;
  Indent?: boolean;
  OutputPathFilters?: PathDefinition[] = [];
  ExportFormat?: string;
  getRequestHeaderValue?: (name: string) => string | undefined;
  constructor() {}
}

export class QueryBase {
  OPath: string;
  Name?: string;
  LimitCount?: number;
  LimitOffset?: number;
  IsDistinct?: boolean;
  DefaultIfEmpty?: boolean;
  Columns: QueryColumn[] = [];
  ObjectQueries?: QuerySpan[] = [];
}

export class Query extends QueryBase {
  ObjectType: string;
  SpatialFilter?: SpatialFilter;

  constructor() {
    super();
  }
}

export class SpatialFilter {
  Point?: number[];
  Line?: number[][];
  Polygon?: number[][][];
  MaxDistanceInMeters?: number;
  PointIdentifierPatterns?: string[];
  LineIdentifierPatterns?: string[];
  PolygonIdentifierPatterns?: string[];
  Extent?: number[];
  Not?: boolean;
  And?: SpatialFilter[];
  Or?: SpatialFilter[];
}

export enum DmlQueryType {
  Update = 0,
  Merge = 1,
  Delete = 2,
}

export class DmlQuery extends Query {
  QueryType: DmlQueryType;
  TargetObjectType?: string;

  constructor() {
    super();
  }
}

export class QuerySpan extends QueryBase {
  constructor() {
    super();
  }
}

export class QueryColumn {
  OPath: string;
  Name: string;
  SortOrder?: QueryColumnSortOrder | string;
  IsFilter?: boolean;
  IsHidden?: boolean;
  IsInvariant?: boolean;
  Format?: string;
  key?: number;
}

export class QueryModelBase extends ModelBase {
  ObjectSpaceName?: string;
  DataSourceId?: string;

  constructor() {
    super();
  }
}

export class QueryObjectsModel
  extends QueryModelBase
  implements ParameterContainer
{
  ObjectQueries: Query[] = [];
  ParameterDefinitions?: ParameterDefinition[] = [];
  Parameters?: ParameterValue[] = [];

  constructor() {
    super();
  }
}

export class QueryValueModel extends QueryModelBase {
  OPath: string;
  Parameters: ParameterValue[] = [];

  constructor() {
    super();
  }
}

/**
 * @deprecated Use `ParameterValue` instead.
 */
export class QueryParameter extends ParameterValue {
  constructor(name: string, value?: any) {
    super(name, value);
  }
}

export class ReadDocumentModel extends ModelBase {
  Id: string;
  IncludeUnusedParameters: boolean;
  constructor() {
    super();
  }
}

export class SaveDocumentModel {
  Id: string;
  Base64Content: string;

  constructor() {}
}

export class SaveQueryToDocumentModel {
  Id: string;
  Query: QueryObjectsModel;

  constructor() {}
}

export class FilterDocumentsModel extends ModelBase {
  Ids: string[] = [];
  XPath: string;
  constructor() {
    super();
  }
}

export class ReadObjectTypeInfoModel extends ModelBase {
  ObjectTypeName: string;
  constructor() {
    super();
  }
}

export class DeleteObjectsModel {
  ObjectKeys: ObjectKey[] = [];
  Behaviour: ObjectDeleteBehaviour;
}

export enum ObjectDeleteBehaviour {
  DeleteAggregateObjects = 1,
  DeleteNonAggregateObjects = 2,
  Default = 1,
  AllReferencingObjects = 3,
}

export enum PersistMode {
  Insert,
  Update,
  Merge,
  Delete,
}

export class PersistModelBase extends QueryModelBase {
  Parameters?: ParameterValue[] = [];
}

export class PersistObjectsModel extends PersistModelBase {
  Objects: TargetObjectData[][];
}

export class PersistObjectModel extends PersistModelBase {
  Object: TargetObjectData;
}

export class TargetObjectData {
  ObjectKey: ObjectKey;
  Mode: PersistMode;
  TargetColumns: TargetColumnValue[] = [];
}

export class ObjectKey {
  Id?: string;
  ObjectType: string | number;
}

export class DataRowInfo {
  Id: string;
  DataSetName: string;
}

export class TargetColumnValue {
  Name: string;
  Value: any;
}

export class ReadFilesModel extends ModelBase {
  Query: QueryValueModel;
  Paths: string[] = [];
}

export class ReadListDataSourceModel extends ModelBase {
  Id: string;
  ObjectKey: ObjectKey;
  ColumnName: string;
  ContextFilterExpression: string;
  ObjectSpaceName: string;
  Parameters: ParameterValue[] = [];
  TargetColumns: TargetColumnValue[] = [];
  CustomFilterExpression: string;
  CustomColumns: CustomColumn[] = [];
}

export class CustomColumn {
  Name: string;
  Caption: string;
  OPath: string;
}

export class ReadPicklistItemsModel extends ModelBase {
  Name: string;
}

export class ReadValidateModel extends ModelBase {
  OPath: string[] = [];
  ObjectTypeName: string;
  constructor() {
    super();
  }
}

export class QueryExpressionModel {
  ObjectType: string;
  OPath: string;
  ObjectSpaceName: string;
  Parameters: ParameterValue[] = [];
  constructor() {}
}

export class ObjectTypeReadInfoModel {
  ObjectTypeName: string;
  OutputMode?: QueryOutputMode;

  constructor() {
    this.OutputMode = QueryOutputMode.Json;
  }
}

export class NetworkTraceModel extends ModelBase {
  EdgeObjectType: string;
  StartEdgeIds: string[] = [];
  EndEdgeIds: string[] = [];
  StartNodeIds: string[] = [];
  EndNodeIds: string[] = [];
  MaxSearchEdgeCount: number;
  StopSearchCondition: StopSearchConditionEnum;
  StartEndEdgesOnly: boolean;
  EdgeFilterMode: EdgeFilterModeEnum;
  EdgeFilterExpression: string;
  EdgeDirection: string;

  constructor() {
    super();
  }
}

export enum EdgeDirection {
  StartToEnd = 'StartToEnd',
  EndToStart = 'EndToStart',
  Both = 'Both',
}

export enum StopSearchConditionEnum {
  None = 0,
  ClosedObjects = 1,
  ClosableObjects = 2,
}

export enum EdgeFilterModeEnum {
  //could be defined somewhere else other than in abc. To be implemented
  None = 0,
  Arbeitsbereich = 1,
  AbwEntwaesserungsgebiet = 2,
  AbwSammler = 3,
}

export class ReportImageInfoQueryDef extends ModelBase {
  Id: string;
  Format: string;

  constructor() {
    super();
    this.Format = '#info';
  }
}

export class ExecuteQueryDocumentDef extends ModelBase {
  Id: string;
  Parameters: ParameterValue[] = [];

  constructor() {
    super();
  }
}

export class ExecuteMoveNodeDef extends ModelBase {
  Id: string;
  ObjectType: string | number;
  NewCoordinate: CoordinateDef;

  constructor() {
    super();
  }
}

export class CoordinateDef {
  X: number;
  Y: number;
  Z: number;
}

export class ValidateModel {
  RefObjectTypeName: string;
  Segments: ValidateSegment[] = [];

  constructor(validateModel) {
    this.RefObjectTypeName = validateModel?.RefObjectTypeName;
    if (validateModel?.Segments?.length > 0) {
      validateModel.Segments.forEach((segment) => {
        this.Segments.push(new ValidateSegment(segment));
      });
    }
  }
}

export class ValidateSegment {
  Name?: string;
  RefObjectTypeName: string;
  ReferencingColumnName?: string;
  ReferencedColumnName?: string;
  Filter: string;
  Type?: 'Cast' | 'Match' | 'Type';

  constructor(segment) {
    Object.assign(this, segment);
  }
}

export class QueryPathModel extends QueryModelBase {
  IdParameterName: string;
  AsInListQuery: boolean;
  ObjectType: string | number;
  OPath: string;
  Parameters: TargetColumnValue[] = [];

  constructor() {
    super();
  }
}

export enum SubscribeForNotificationsType {
  WebPush = 'WebPush',
  GoogleFirebase = 'GoogleFirebase',
}

export abstract class SubscribeForNotificationsModelBase {
  Type: SubscribeForNotificationsType;
  ClientIdentifier: string;
  ChannelIdentifier: string;
}

export class SubscribeForNotificationsModel extends SubscribeForNotificationsModelBase {
  ProviderDefinition: object;
  ContextDefinition?: object;
}

export class UnsubscribeForNotificationsModel extends SubscribeForNotificationsModelBase {}

export class SendNotificationModel {
  UserOrGroupNames: string[] = [];
  ChannelIdentifiers: string[] = [];
  Data: NotificationDataPayload;

  constructor() {
    this.Data = new NotificationDataPayload();
  }
}

export class NotificationDataPayload {
  data: NotificationData;
}
export class NotificationData {
  body: string;
  title: string;
  [key: string]: string;
}

export class LicenseFeatureInfo {
  AppIdentifier: string;
  FeatureName: string;
}

export class CheckLicensesModel extends ModelBase {
  Features: LicenseFeatureInfo[] = [];
}

export class ObjectData {
  ObjectKey: ObjectKey;
  TargetColumns: TargetColumnValue[] = [];
}

export class EvaluateValue extends QueryModelBase {
  OPath: string;
  Object: ObjectData;
}

export abstract class EvaluateModelBase extends QueryModelBase {
  Object: ObjectData;
}

export class EvaluateValues extends EvaluateModelBase {
  Values: ValueDefinition[] = [];
}

export class EvaluateDefaultValues extends EvaluateModelBase {
  ContextFilterExpressions: string[] = [];
}

export class EvaluateObjectModel extends EvaluateModelBase {
  OPath: string;
  IdParameterName: string;
  AsInListQuery?: boolean;
}
